import { alpha } from '@precis-digital/kurama/utils'
import { type CSSObject, type Theme } from '@precis-digital/kurama/theme'
import {
  buildCopilotAiStrongConicGradient,
  buildCopilotAiSubtleConicGradient,
  COPILOT_ACTION_HIGHLIGHT_ATTRIBUTE,
  COPILOT_ACTION_HIGHLIGHT_STYLE_ID,
  COPILOT_ACTION_GRADIENT_ROTATION_START_DEG,
  COPILOT_ACTION_GRADIENT_ROTATION_TOTAL_DEG,
  COPILOT_ACTION_HIGHLIGHT_ANIMATION_DURATION_MS,
  COPILOT_ACTION_HIGHLIGHT_KEYFRAME_STOPS,
  COPILOT_ACTION_RING_WIDTH_PX,
  COPILOT_AI_FOCUS_SHADOW_BLUR_PX,
  COPILOT_AI_FOCUS_SHADOW_COLOR,
  COPILOT_ELEMENT_HIGHLIGHT_LOOP_DURATION_MS,
  COPILOT_FOCUS_HIGHLIGHT_ATTRIBUTE,
  COPILOT_FOCUS_HIGHLIGHT_STYLE_ID,
  COPILOT_FOCUS_PULSE_ITERATIONS,
  COPILOT_FOCUS_RING_WIDTH_PX
} from './copilotElementHighlight'
import { buildCopilotHighlightRingBeforeStyles, injectCopilotStylesheet } from './copilotElementHighlightCss'

const COPILOT_FOCUS_PULSE_SHADOW_NONE = `0 0 0 0 ${alpha(COPILOT_AI_FOCUS_SHADOW_COLOR, 0)}`
const COPILOT_FOCUS_PULSE_SHADOW_PEAK = `0 0 ${COPILOT_AI_FOCUS_SHADOW_BLUR_PX}px 0 ${alpha(
  COPILOT_AI_FOCUS_SHADOW_COLOR,
  0.6
)}`

const buildCopilotFocusHighlightStyles = (theme: Theme): CSSObject => ({
  '@keyframes copilot-focus-highlight-pulse-shadow': {
    '0%, 100%': {
      boxShadow: COPILOT_FOCUS_PULSE_SHADOW_NONE
    },
    '50%': {
      boxShadow: COPILOT_FOCUS_PULSE_SHADOW_PEAK
    }
  },
  [`[${COPILOT_FOCUS_HIGHLIGHT_ATTRIBUTE}]`]: {
    position: 'relative',
    isolation: 'isolate'
  },
  ...buildCopilotHighlightRingBeforeStyles({
    highlightAttribute: COPILOT_FOCUS_HIGHLIGHT_ATTRIBUTE,
    ringVariablePrefix: 'focus',
    ringWidthPx: COPILOT_FOCUS_RING_WIDTH_PX,
    background: buildCopilotAiSubtleConicGradient(theme)
  }),
  [`[${COPILOT_FOCUS_HIGHLIGHT_ATTRIBUTE}]::after`]: {
    content: '""',
    position: 'absolute',
    inset: `calc(-1 * var(--copilot-focus-ring-outset, ${COPILOT_FOCUS_RING_WIDTH_PX}px))`,
    borderRadius: `calc(var(--copilot-focus-border-radius, 0px) + var(--copilot-focus-ring-outset, ${COPILOT_FOCUS_RING_WIDTH_PX}px))`,
    boxShadow: COPILOT_FOCUS_PULSE_SHADOW_NONE,
    animation: `copilot-focus-highlight-pulse-shadow var(--copilot-focus-pulse-duration, ${COPILOT_ELEMENT_HIGHLIGHT_LOOP_DURATION_MS}ms) ease-in-out var(--copilot-focus-pulse-iterations, ${COPILOT_FOCUS_PULSE_ITERATIONS}) forwards`,
    pointerEvents: 'none',
    zIndex: 0
  }
})

const buildCopilotActionHighlightShiftKeyframes = (theme: Theme): CSSObject => {
  const frames: Record<string, CSSObject> = {}

  for (let stop = 0; stop <= COPILOT_ACTION_HIGHLIGHT_KEYFRAME_STOPS; stop++) {
    const progress = stop / COPILOT_ACTION_HIGHLIGHT_KEYFRAME_STOPS
    const label = stop === COPILOT_ACTION_HIGHLIGHT_KEYFRAME_STOPS ? '100%' : `${progress * 100}%`
    const degrees = COPILOT_ACTION_GRADIENT_ROTATION_START_DEG + progress * COPILOT_ACTION_GRADIENT_ROTATION_TOTAL_DEG

    frames[label] = {
      backgroundImage: buildCopilotAiStrongConicGradient(theme, `${degrees}deg`)
    }
  }

  return frames
}

const buildCopilotActionHighlightStyles = (theme: Theme): CSSObject => ({
  '@keyframes copilot-action-highlight-shift-colors': buildCopilotActionHighlightShiftKeyframes(theme),
  [`[${COPILOT_ACTION_HIGHLIGHT_ATTRIBUTE}]`]: {
    position: 'relative',
    isolation: 'isolate'
  },
  ...buildCopilotHighlightRingBeforeStyles({
    highlightAttribute: COPILOT_ACTION_HIGHLIGHT_ATTRIBUTE,
    ringVariablePrefix: 'action',
    ringWidthPx: COPILOT_ACTION_RING_WIDTH_PX,
    background: buildCopilotAiStrongConicGradient(theme, `${COPILOT_ACTION_GRADIENT_ROTATION_START_DEG}deg`),
    extraStyles: {
      animation: `copilot-action-highlight-shift-colors var(--copilot-action-highlight-duration, ${COPILOT_ACTION_HIGHLIGHT_ANIMATION_DURATION_MS}ms) linear 1 forwards`,
      willChange: 'background-image'
    }
  })
})

let copilotFocusHighlightStylesInjected = false
let copilotActionHighlightStylesInjected = false

/**
 * Injects focus highlight keyframes and ring styles once per page load.
 * Guarded by a module flag so repeated calls are cheap no-ops.
 */
export const ensureCopilotFocusHighlightStyles = (theme: Theme): void => {
  if (copilotFocusHighlightStylesInjected) return

  injectCopilotStylesheet(COPILOT_FOCUS_HIGHLIGHT_STYLE_ID, buildCopilotFocusHighlightStyles(theme))
  copilotFocusHighlightStylesInjected = true
}

/**
 * Injects action highlight keyframes and rotating gradient ring styles once per page load.
 * Guarded by a module flag so repeated calls are cheap no-ops.
 */
export const ensureCopilotActionHighlightStyles = (theme: Theme): void => {
  if (copilotActionHighlightStylesInjected) return

  injectCopilotStylesheet(COPILOT_ACTION_HIGHLIGHT_STYLE_ID, buildCopilotActionHighlightStyles(theme))
  copilotActionHighlightStylesInjected = true
}
